import React, { useMemo, useEffect, useState } from 'react';
import { useAppContext } from '../../contexts/AppContext';
import { Truck, MaintenanceEntry } from '../../types';
import { db } from '../../config/firebase';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';

const Esp32ReadingCard: React.FC = () => {
  const [reading, setReading] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadLatestReading = async () => {
      try {
        const readingsQuery = query(
          collection(db, 'esp32Readings'),
          orderBy('createdAt', 'desc'),
          limit(1)
        );
        const snapshot = await getDocs(readingsQuery);
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          const data = doc.data();
          setReading({
            deviceId: data.deviceId || 'test-device-001',
            temperature: data.temperature,
            vibration: data.vibration,
            healthScore: data.healthScore,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
          });
        }
      } catch (err: any) {
        setError(err?.message || 'Unable to load ESP32 reading');
      }
    };

    loadLatestReading();
  }, []);

  const formatDate = (value: any) => {
    if (!value) return 'No timestamp';
    const date = value instanceof Date ? value : new Date(value);
    return date.toLocaleString();
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Latest ESP32 Reading</h3>
      </div>
      <div className="card-body">
        {error && <p className="text-red-600">{error}</p>}
        {!reading && !error && <p>Loading latest reading...</p>}
        {reading && (
          <div className="space-y-3">
            <div>
              <strong>Device:</strong> {reading.deviceId}
            </div>
            <div>
              <strong>Temperature:</strong> {reading.temperature}°C
            </div>
            <div>
              <strong>Vibration:</strong> {reading.vibration} mm/s
            </div>
            <div>
              <strong>Health Score:</strong> {reading.healthScore}%
            </div>
            <div>
              <strong>Last updated:</strong> {formatDate(reading.createdAt)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { state } = useAppContext();
  const { trucks, maintenance, currentUser } = state;
  const isEspAdmin = currentUser?.email === 'argamtaha2009@gmail.com';

  const dashboardStats = useMemo(() => {
    if (!trucks || !maintenance) return {
      activeTrucks: 0,
      trucksInMaintenance: 0,
      retiredTrucks: 0,
      pendingMaintenance: 0,
      totalTrucks: 0
    };

    return {
      activeTrucks: trucks.filter((t: Truck) => t.status === 'In Service').length,
      trucksInMaintenance: trucks.filter((t: Truck) => t.status === 'Out for Repair').length,
      retiredTrucks: trucks.filter((t: Truck) => t.status === 'Retired').length,
      pendingMaintenance: trucks.filter((t: Truck) => t.status === 'Needs Attention').length,
      totalTrucks: trucks.length
    };
  }, [trucks, maintenance]);



  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>FleetFix Dashboard</h1>
        <p>Fleet management at your fingertips</p>
      </div>

      <div className="grid grid-cols-4 mb-6">
        <div className="card">
          <div className="card-body text-center">
            <div className="stat-number">{dashboardStats.totalTrucks}</div>
            <div className="stat-label">Total Trucks</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <div className="stat-number text-green-600">{dashboardStats.activeTrucks}</div>
            <div className="stat-label">In Service</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <div className="stat-number text-red-600">{dashboardStats.trucksInMaintenance}</div>
            <div className="stat-label">In Maintenance</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <div className="stat-number text-yellow-600">{dashboardStats.pendingMaintenance}</div>
            <div className="stat-label">Pending Maintenance</div>
          </div>
        </div>
      </div>

      {isEspAdmin && (
        <div className="grid grid-cols-2 gap-6 mb-6">
          <Esp32ReadingCard />
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recent Maintenance</h3>
          </div>
          <div className="card-body">
            {(() => {
              // Show all maintenance entries regardless of date - sort by most recent first
              const sortedMaintenance = maintenance?.slice().sort((a: MaintenanceEntry, b: MaintenanceEntry) => 
                new Date(b.date).getTime() - new Date(a.date).getTime()
              ) || [];
              
              return sortedMaintenance.slice(0, 5).map((entry: MaintenanceEntry) => (
                <div key={entry.id} className="maintenance-item">
                  <div className="flex justify-between">
                    <span>{entry.type}</span>
                    <span>{entry.cost ? `$${entry.cost}` : 'No cost'}</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(entry.date).toLocaleDateString()}
                  </div>
                </div>
              ));
            })()}
            {(() => {
              const sortedMaintenance = maintenance?.slice().sort((a: MaintenanceEntry, b: MaintenanceEntry) => 
                new Date(b.date).getTime() - new Date(a.date).getTime()
              ) || [];
              
              return sortedMaintenance.length === 0 ? <p>No maintenance entries found</p> : null;
            })()}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Fleet Overview</h3>
          </div>
          <div className="card-body">
            {trucks?.slice(0, 5).map((truck: Truck) => (
              <div key={truck.id} className="truck-item">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-semibold">{truck.licensePlate}</span>
                    <div className="text-sm text-gray-500">
                      {truck.make} {truck.model} ({truck.year})
                    </div>
                  </div>
                  <span className={`status-badge status-${truck.status.toLowerCase().replace(' ', '-')}`}>
                    {truck.status}
                  </span>
                </div>
              </div>
            )) || <p>No trucks found</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
